import React, {useCallback, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {useSiteNavigate} from "nav/SiteLink";
import {useMessages} from "i18n";
import {usePanelOpenLatency} from "perf/panelLatency";
import {CardSheet} from "components/CardSheet";
import {NAV_PILL_BAR_SELECTOR} from "nav/navPillBar";
import {PANEL_CLOSE_TARGET} from "./featurePanelClose";

/**
 * The feature sheet the top-pill destinations open (ui#150, re-cut in ui#1038 and ui#1054).
 *
 * A URL-driven surface: the App shell mounts exactly one of these while the route is a
 * {@link isFeaturePanelRoute} one, and the ROUTE — not local state — decides which sheet is open. Deep
 * links and refreshes therefore render the home canvas with this sheet already open, and closing it
 * navigates back to {@link PANEL_CLOSE_TARGET}.
 *
 * The presentation is the shared {@link CardSheet}: a floating card below the top pill bar, at the
 * bar's own elevation, sliding in from the right — the same surface the Personalise editor uses since
 * ui#1054. It is NON-MODAL, which is what keeps destination switching seamless: the bar stays visible
 * and clickable above the sheet, so clicking another pill swaps the destination in place. The App keeps
 * this instance mounted across sibling feature routes (tab semantics) and only the body crossfades,
 * keyed on the pathname — no shell remount, no reload.
 *
 * All this component owns is the route half of the contract: hold the open flag, turn "the sheet has
 * finished sliding out" into the navigation home, and name the pill bar as the region this surface is
 * OPERATED from (ui#1534, at the render call below). The sequencing, focus handling, Escape and the
 * outside-click dismissal all live in the shared sheet, so every card sheet answers them identically.
 */
export interface FeaturePanelProps {
	/** Visible sheet title AND the region's accessible name — catalog copy, resolved by the App shell. */
	title: string;
	/** Optional localized substring of the title rendered with the shared CardSheet accent treatment. */
	titleAccent?: string;
	/** Optional supporting copy rendered directly beneath the sheet title. */
	subtitle?: string;
	/** The active feature route's body, injected by the App shell as the router `<Outlet/>`. */
	children: React.ReactNode;
}

export function FeaturePanel({title, titleAccent, subtitle, children}: FeaturePanelProps) {
	const {common} = useMessages();
	const navigate = useSiteNavigate();
	const location = useLocation();

	// ui#169: single instrumentation seam — resolves a pending panel-route-intent mark to "panel body
	// first paint" for this pathname (the body remounts per pathname). All logic lives in perf/panelLatency.
	usePanelOpenLatency(location.pathname);

	// Open from the moment the route mounts us; closing is what navigates away.
	const [open, setOpen] = useState(true);
	// One-shot guard so a ✕ + Escape race can only navigate once.
	const navigatedRef = useRef(false);

	const requestClose = useCallback(() => setOpen(false), []);

	// Deferred to the end of the exit transition by the shared sheet, so the route never changes under a
	// still-animating card.
	const onExited = useCallback(() => {
		if (navigatedRef.current) {
			return;
		}
		navigatedRef.current = true;
		navigate(PANEL_CLOSE_TARGET, {replace: true});
	}, [navigate]);

	return (
		<CardSheet
			open={open}
			onClose={requestClose}
			title={title}
			titleAccent={titleAccent}
			subtitle={subtitle}
			closeLabel={common.close}
			role="region"
			// The pill bar is THIS sheet's own tab strip, so a click there is not a click on the page
			// behind it (ui#1534). The other card sheets are transient editors raised from a button and
			// operated entirely inside their own card; this one is a ROUTE, and the App keeps this
			// instance mounted across sibling feature routes, crossfading only the body (ui#150,
			// ui#1038, ui#1054) — which is what makes switching destination seamless.
			//
			// Without this the sheet would dismiss on its own controls, and the sender would not simply
			// lose an animation: `onExited` navigates to PANEL_CLOSE_TARGET, so the route would reach the
			// destination the pill points at and the exit's `replace` would take it straight back to the
			// canvas behind, remounting this shell on the way through. That is the loss the sheet was
			// exempted from light dismiss for until ui#1534, and naming the bar is what makes the
			// exemption unnecessary: `e2e/card-sheet-dismiss.spec.ts` asserts the pill still switches
			// destination in the SAME shell node, and that a click on the page DOES dismiss.
			ownControlsSelector={NAV_PILL_BAR_SELECTOR}
			onExited={onExited}
			// Keyed by pathname so a destination switch remounts + fades the body, while the card shell
			// and the canvas behind it persist.
			bodyKey={location.pathname}
			testId="feature-panel"
		>
			{children}
		</CardSheet>
	);
}
